import React from 'react';

import { ColumnDef } from '@tanstack/react-table';
import colorsVars from 'styles/colors.module.scss';

import { ITableColumnState } from 'hooks/useTableCurrentState';

import { PivotTableGroupDetails } from 'api/endPoints/getPivotTable';

import { CustomizedStatusBar, Data } from 'components/common/CustomizedStatusBar/CustomizedStatusBar';
import { TitleAndSubTitle } from 'components/common/Tables/Cells/TitleAndSubTitle/TitleAndSubTitle';
import { columnsCommonConfig } from 'components/common/Tables/columnsCommonConfig';

import { GroupByColumnHeader } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/PivotBoard/components/GroupByTable/components/GroupByColumnHeader/GroupByColumnHeader';
import { ExpandedTitleColumn } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/PivotBoard/components/GroupByTable/components/cells/ExpandedTitleColumn/ExpandedTitleColumn';
import { ValueWithPercentSign } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/PivotBoard/components/GroupByTable/components/cells/ValueWithPercentSign/ValueWithPercentSign';

const { todoCardBackground, inProgressCardBackground, doneCardBackground } = colorsVars;

export const getGroupedByTableColumns = (
    primaryGroupSelected,
    secondaryGroupSelected,
    totalGroupsSizeWarning,
): ColumnDef<PivotTableGroupDetails>[] => {
    return [
        {
            header: () => (
                <TitleAndSubTitle
                    title={
                        <GroupByColumnHeader
                            primaryGroupLabel={primaryGroupSelected}
                            secondaryGroupLabel={secondaryGroupSelected}
                        />
                    }
                    subTitle={'GROUPED BY'}
                />
            ),
            ...columnsCommonConfig.pivotTableGroupByName,
            accessorKey: 'name',
            meta: {
                columnLabel: 'Grouped by',
            },
            cell: ({ row }) => {
                const name = row.original.name;
                const numberOfTasks = row.original.taskNum;
                const title = `${name} (${numberOfTasks})`;
                return (
                    <ExpandedTitleColumn
                        title={title}
                        canExpand={row.getCanExpand()}
                        isExpanded={row.getIsExpanded()}
                        toggleExpandedHandler={row.getToggleExpandedHandler()}
                    />
                );
            },
        },
        {
            header: () => <TitleAndSubTitle title={'Status'} subTitle={'Summary'} />,
            ...columnsCommonConfig.pivotTableGroupByStatus,
            accessorKey: 'taskNum',
            meta: {
                columnLabel: 'Status',
            },
            cell: ({ row }) => {
                const obj: Data = {
                    todo: {
                        label: 'To Do (Percentage)',
                        color: todoCardBackground,
                        count: Number(row.original.todoPercent),
                    },
                    inProgress: {
                        label: 'In Progress (Percentage)',
                        color: inProgressCardBackground,
                        count: Number(row.original.inProgressPercent),
                    },
                    done: {
                        label: 'Done (Percentage)',
                        color: doneCardBackground,
                        count: Number(row.original.donePercent),
                    },
                };

                return <CustomizedStatusBar data={obj} />;
            },
        },
        {
            header: () => <TitleAndSubTitle title={'Status: To Do'} subTitle={'Percent'} />,
            ...columnsCommonConfig.pivotTableGroupByStatusToDo,
            accessorKey: 'todoPercent',
            meta: {
                columnLabel: 'Status: To Do',
            },
            cell: ({ row }) => {
                return <ValueWithPercentSign value={row.original.todoPercent} />;
            },
        },
        {
            header: () => <TitleAndSubTitle title={'Status: In Progress'} subTitle={'Percent'} />,
            ...columnsCommonConfig.pivotTableGroupByStatusInProgress,
            accessorKey: 'inProgressPercent',
            meta: {
                columnLabel: 'Status: In Progress',
            },
            cell: ({ row }) => {
                return <ValueWithPercentSign value={row.original.inProgressPercent} />;
            },
        },
        {
            header: () => <TitleAndSubTitle title={'Status: Done'} subTitle={'Percent'} />,
            ...columnsCommonConfig.pivotTableGroupByStatusDone,
            accessorKey: 'donePercent',
            meta: {
                columnLabel: 'Status: Done',
            },
            cell: ({ row }) => {
                return <ValueWithPercentSign value={row.original.donePercent} />;
            },
        },
        {
            header: () => <TitleAndSubTitle title={'Critical Path'} subTitle={'Percent'} />,
            ...columnsCommonConfig.criticalPath,
            accessorKey: 'criticalPathPercent',
            meta: {
                columnLabel: 'Critical Path',
            },
            cell: ({ row }) => {
                return <ValueWithPercentSign value={row.original.criticalPathPercent?.toString()} />;
            },
        },
        {
            header: () => <TitleAndSubTitle title={'Finish Variance (Avg)'} subTitle={'Avg'} />,
            ...columnsCommonConfig.pivotTableFinishVariance,
            accessorKey: 'lateFinishAvg',
            meta: {
                columnLabel: 'Finish Variance (Avg)',
            },
        },
        {
            header: () => <TitleAndSubTitle title={'Total Float'} subTitle={'Avg'} />,
            ...columnsCommonConfig.totalFloatAvg,
            meta: {
                columnLabel: 'Total Float',
            },
            accessorKey: 'totalFloatAvg',
        },
        {
            header: () => <TitleAndSubTitle title={'Start Variance'} subTitle={'Avg'} />,
            ...columnsCommonConfig.lateStartAvg,
            meta: {
                columnLabel: 'Start Variance',
            },
            accessorKey: 'lateStartAvg',
        },
        {
            header: () => <TitleAndSubTitle title={'Start Variance %Delayed'} subTitle={'% Delayed start'} />,
            ...columnsCommonConfig.lateStartDelayedPercent,
            accessorKey: 'lateStartDelayedPercent',
            meta: {
                columnLabel: 'Start Variance %Delayed',
            },
            cell: ({ row }) => {
                return <ValueWithPercentSign value={row.original.lateStartDelayedPercent?.toString()} />;
            },
        },
        {
            header: () => <TitleAndSubTitle title={'Start Variance %Early'} subTitle={'% Early start'} />,
            ...columnsCommonConfig.lateStartEarlyPercent,
            accessorKey: 'lateStartEarlyPercent',
            meta: {
                columnLabel: 'Start Variance %Early',
            },
            cell: ({ row }) => {
                return <ValueWithPercentSign value={row.original.lateStartEarlyPercent?.toString()} />;
            },
        },
        {
            header: () => <TitleAndSubTitle title={'Finish Variance %Delayed'} subTitle={'% Delayed finish'} />,
            ...columnsCommonConfig.lateFinishDelayedPercent,
            accessorKey: 'lateFinishDelayedPercent',
            meta: {
                columnLabel: 'Finish Variance %Delayed',
            },
            cell: ({ row }) => {
                return <ValueWithPercentSign value={row.original.lateFinishDelayedPercent?.toString()} />;
            },
        },
        {
            header: () => <TitleAndSubTitle title={'Finish Variance %Early'} subTitle={'% Early finish'} />,
            ...columnsCommonConfig.lateFinishEarlyPercent,
            accessorKey: 'lateFinishEarlyPercent',
            meta: {
                columnLabel: 'Finish Variance %Early',
            },
            cell: ({ row }) => {
                return <ValueWithPercentSign value={row.original.lateFinishEarlyPercent.toString()} />;
            },
        },
        {
            header: () => <TitleAndSubTitle title={'Duration Overrun'} subTitle={'Avg'} />,
            ...columnsCommonConfig.durationAvg,
            meta: {
                columnLabel: 'Duration Overrun',
            },
            accessorKey: 'durationAvg',
        },
        {
            header: () => <TitleAndSubTitle title={'Duration %Overrun'} subTitle={''} />,
            ...columnsCommonConfig.durationOverrun,
            meta: {
                columnLabel: 'Duration %Overrun',
            },
            accessorKey: 'durationOverrunPercent',
        },
        {
            header: () => <TitleAndSubTitle title={'Duration %Underrun'} subTitle={''} />,
            ...columnsCommonConfig.durationUnderrunPercent,
            accessorKey: 'durationUnderrunPercent',
            meta: {
                columnLabel: 'Duration %Underrun',
            },
            cell: ({ row }) => {
                return <ValueWithPercentSign value={row.original.durationUnderrunPercent.toString()} />;
            },
        },
        {
            header: () => <TitleAndSubTitle title={'Budgeted Resource Units'} subTitle={'Median'} />,
            ...columnsCommonConfig.budgetResourceMedian,
            meta: {
                columnLabel: 'Budgeted Resource Units (Median)',
            },
            accessorKey: 'budgetResourceMedian',
        },
        {
            header: () => <TitleAndSubTitle title={'Actual Resource Units'} subTitle={'Median'} />,
            ...columnsCommonConfig.actualResource,
            meta: {
                columnLabel: 'Actual Resource Units (Median)',
            },
            accessorKey: 'actualResourceMedian',
        },
        {
            header: () => <TitleAndSubTitle title={'Remaining Resource Units'} subTitle={'Median'} />,
            ...columnsCommonConfig.remainingResource,
            meta: {
                columnLabel: 'Remaining Resource Units (Median)',
            },
            accessorKey: 'remainingResourceMedian',
        },
        {
            header: () => <TitleAndSubTitle title={'Budgeted Resource Units'} subTitle={'Sum'} />,
            ...columnsCommonConfig.budgetResourceSum,
            meta: {
                columnLabel: 'Budgeted Resource Units (Sum)',
            },
            accessorKey: 'budgetResourceSum',
        },
        {
            header: () => <TitleAndSubTitle title={'Actual Resource Units'} subTitle={'Sum'} />,
            ...columnsCommonConfig.actualResourceSum,
            meta: {
                columnLabel: 'Actual Resource Units (Sum)',
            },
            accessorKey: 'actualResourceSum',
        },
        {
            header: () => <TitleAndSubTitle title={'Remaining Resource Units'} subTitle={'Sum'} />,
            ...columnsCommonConfig.remainingResourceSum,
            meta: {
                columnLabel: 'Remaining Resource Units (Sum)',
            },
            accessorKey: 'remainingResourceSum',
        },
        {
            header: () => <TitleAndSubTitle title={'Criticality Score'} subTitle={'Median'} />,
            ...columnsCommonConfig.criticalScoreMedian,
            meta: {
                columnLabel: 'Criticality Score',
            },
            accessorKey: 'criticalScoreMedian',
        },
        {
            header: () => <TitleAndSubTitle title={'Completed Duration (P50)'} subTitle={'P50'} />,
            ...columnsCommonConfig.completedDurationMedian,
            meta: {
                columnLabel: 'Completed Duration (P50)',
            },
            accessorKey: 'completedDurationMedian',
        },
        {
            header: () => <TitleAndSubTitle title={'Completed Duration'} subTitle={'P80'} />,
            ...columnsCommonConfig.pivotTableCompletedDurationP80,
            meta: {
                columnLabel: 'Completed Duration',
            },
            accessorKey: 'completedDurationP80',
        },
        {
            header: () => (
                <TitleAndSubTitle
                    title={'Planned Progress %'}
                    subTitle={'Summary'}
                    disabled={Boolean(totalGroupsSizeWarning)}
                />
            ),
            ...columnsCommonConfig.criticalityPlannedPercent,
            accessorKey: 'criticalityPlannedPercent',
            meta: {
                columnLabel: 'Planned Progress %',
            },
            enableSorting: !Boolean(totalGroupsSizeWarning),
            cell: ({ row }) => {
                return totalGroupsSizeWarning ? (
                    ' - '
                ) : (
                    <ValueWithPercentSign value={row.original.criticalityPlannedPercent?.toString()} />
                );
            },
        },
        {
            header: () => (
                <TitleAndSubTitle
                    title={'Actual Progress %'}
                    subTitle={'Summary'}
                    disabled={Boolean(totalGroupsSizeWarning)}
                />
            ),
            ...columnsCommonConfig.criticalityActualPercent,
            accessorKey: 'criticalityActualPercent',
            meta: {
                columnLabel: 'Actual Progress %',
            },
            enableSorting: !Boolean(totalGroupsSizeWarning),
            cell: ({ row }) => {
                return totalGroupsSizeWarning ? (
                    ' - '
                ) : (
                    <ValueWithPercentSign value={row.original.criticalityActualPercent?.toString()} />
                );
            },
        },
        {
            header: () => (
                <TitleAndSubTitle title={'SPI'} subTitle={'Summary'} disabled={Boolean(totalGroupsSizeWarning)} />
            ),
            ...columnsCommonConfig.criticalityScheduleAdherence,
            meta: {
                columnLabel: 'SPI',
            },
            accessorKey: 'criticalityScheduleAdherence',
            enableSorting: !Boolean(totalGroupsSizeWarning),
            cell: ({ getValue }) => {
                return totalGroupsSizeWarning ? ' - ' : getValue();
            },
        },
    ];
};

export const initialState: ITableColumnState = {
    hiddenColumns: [
        'pivotTableFinishVariance',
        'actualResource',
        'totalFloatAvg',
        'lateStartAvg',
        'lateStartDelayedPercent',
        'lateStartEarlyPercent',
        'lateFinishDelayedPercent',
        'lateFinishEarlyPercent',
        'durationAvg',
        'durationOverrun',
        'durationUnderrunPercent',
        'budgetResourceMedian',
        'remainingResource',
        'budgetResourceSum',
        'remainingResourceSum',
        'actualResourceSum',
        'criticalScoreMedian',
        'completedDurationMedian',
        'pivotTableCompletedDurationP80',
        'criticalityPlannedPercent',
        'criticalityActualPercent',
        'criticalityScheduleAdherence',
    ],
    sortBy: [],
};
